/**
 * Lightweight chronological comparator for Knocknoc native-version gating and
 * OTA downgrade protection.
 *
 * Understands two shapes:
 *   - Clean CalVer used by the native app: `X.Y.Z` and `X.Y.Z-betaN`
 *     (a dash-beta is a pre-final build, so it sorts BEFORE `X.Y.Z`).
 *   - The server's chronological package scheme used for OTA build versions:
 *     `X.Y.Z~C.betaN` and `X.Y.Z~C.gHASH`, where C is the commit count since the
 *     last final. These are pre-final builds of `X.Y.Z` too, ordered by C, so
 *     they also sort before `X.Y.Z`.
 *
 * Trailing dev-build noise on the legacy form (commits, git hash, `.dirty`) is
 * intentionally ignored -- it should not gate production UX.
 *
 * If either side is unparseable (e.g. "LOCAL BUILD", "1.0", empty string),
 * compareVersions returns null. Callers should treat null as "cannot compare,
 * do not gate" to fail open on dev builds / bad input.
 */

interface ParsedVersion {
  major: number;
  minor: number;
  patch: number;
  // A pre-final build (beta or prerelease) heading towards major.minor.patch;
  // sorts before the final of the same version.
  pre: boolean;
  // Tiebreak among pre-finals of the same major.minor.patch: the beta number for
  // the dash form, or the commit count C for the tilde form. (The two forms are
  // never mixed within a single comparison: the native gate only sees CalVer and
  // OTA only sees the tilde/final scheme.)
  order: number;
}

// Server "tilde" scheme: X.Y.Z~C.betaN or X.Y.Z~C.gHASH.
const TILDE_REGEX = /^(\d+)\.(\d+)\.(\d+)~(\d+)\.(?:beta\d+|g[0-9a-fA-F]+)/;
// Legacy / clean CalVer: X.Y.Z optionally -betaN; trailing commits/hash/dirty ignored.
const VERSION_REGEX = /^(\d+)\.(\d+)\.(\d+)(?:-beta(\d+))?(?:$|[-.])/;

function parseVersion(v: string): ParsedVersion | null {
  if (!v) return null;
  const s = v.trim();

  const tilde = TILDE_REGEX.exec(s);
  if (tilde) {
    return {
      major: Number(tilde[1]),
      minor: Number(tilde[2]),
      patch: Number(tilde[3]),
      pre: true,
      order: Number(tilde[4]),
    };
  }

  const m = VERSION_REGEX.exec(s);
  if (!m) return null;
  const beta = m[4] !== undefined ? Number(m[4]) : null;
  return {
    major: Number(m[1]),
    minor: Number(m[2]),
    patch: Number(m[3]),
    pre: beta !== null,
    order: beta ?? 0,
  };
}

/**
 * Returns a negative number if a < b, zero if equal, positive if a > b, or null
 * if either version is unparseable. A pre-final build (beta or prerelease) is
 * less than the corresponding final (`1.2.3-beta1` < `1.2.3`,
 * `26.06.2~64.beta8` < `26.06.2`); pre-finals of the same version order by their
 * beta number (dash form) or commit count (tilde form).
 */
export function compareVersions(a: string, b: string): number | null {
  const pa = parseVersion(a);
  const pb = parseVersion(b);
  if (pa === null || pb === null) return null;

  if (pa.major !== pb.major) return pa.major - pb.major;
  if (pa.minor !== pb.minor) return pa.minor - pb.minor;
  if (pa.patch !== pb.patch) return pa.patch - pb.patch;

  // A pre-final build sorts before the final of the same X.Y.Z.
  if (pa.pre !== pb.pre) return pa.pre ? -1 : 1;
  if (pa.pre && pb.pre) return pa.order - pb.order;

  return 0;
}

/**
 * Decides whether the running native app must be force-updated because it is
 * older than the server's required minimum. The single source of truth for
 * the force-update gate, with deliberately fail-open semantics:
 *
 *  - Empty / whitespace `minNativeVersion` means no minimum is set, so every
 *    version is compatible -> returns false.
 *  - An unknown native version (plugin error / not native) -> returns false.
 *  - Either side unparseable as `X.Y.Z[-betaN]` (e.g. dev builds like
 *    "LOCAL BUILD", or a misconfigured 2-part minimum) -> returns false, so we
 *    never wall users in on something we cannot compare.
 *  - Otherwise returns true only when nativeVersion < minNativeVersion.
 */
export function isBelowMinimumVersion(
  nativeVersion: string | null | undefined,
  minNativeVersion: string | null | undefined,
): boolean {
  if (!minNativeVersion || !minNativeVersion.trim()) return false;
  if (!nativeVersion || !nativeVersion.trim()) return false;
  const cmp = compareVersions(nativeVersion, minNativeVersion);
  if (cmp === null) return false;
  return cmp < 0;
}
